// © Microsoft Corporation. All rights reserved.

import { PopOverHandledProps, PopOverProps, PopOverUnhandledProps } from "./PopOver.props";
import { keyCodeEscape, keyCodeTab } from "@microsoft/fast-web-utilities";

import { DesignSystem } from "@microsoft/fast-components-styles-msft";
import { DesignSystemConsumer } from "@microsoft/fast-jss-manager-react";
import { Direction } from "@microsoft/fast-web-utilities";
import Foundation from "@microsoft/fast-components-foundation-react";
import { HandledPropsKeys, getContextData } from "@msnews/core";
import React from "react";
import ReactDOM from "react-dom";
import Tabbable from "tabbable";

interface PositionStyles {
    /** Top offset, in px */
    top: number;

    /** Left offset, in px */
    left?: number;

    /** Right offset, in px */
    right?: number;
}

/**
 * PopOver component
 * @class - PopOver
 * @classdesc - The PopOver component. Renders a popover menu on a new portal. Position to be defined by top/left props
 * @default
 */
class PopOver extends Foundation<PopOverHandledProps, PopOverUnhandledProps, { width: number, height: number }> {
    /**
     * The Pop Over handled prop contract
     * Describes the object that enumerates all handled props for a component. This
     * object includes all props that can in some way consumed or manipulated by component
     * code.
     * @protected
     * @type { HandledPropsKeys<PopOverHandledProps> }
     */
    protected handledProps: HandledPropsKeys<PopOverHandledProps> = {
        anchor: undefined,
        autoFlipX: undefined,
        autoFocus: undefined,
        children: undefined,
        enable: undefined,
        left: undefined,
        managedClasses: undefined,
        offset: undefined,
        onDismiss: undefined,
        requestContext: undefined,
        staticPosition: undefined,
        top: undefined,
        zIndex: undefined
    };

    /** Reference for the PopOver div element */
    private popOverRef: React.RefObject<HTMLDivElement>;

    constructor(props: PopOverProps) {
        super(props);
        this.popOverRef = React.createRef();
        this.state = {
            width: 0,
            height: 0
        };
    }

    /**
     * Component did mount
     */
    public componentDidMount(): void {
        if (this.popOverRef && this.popOverRef.current) {
            const { width, height } = this.popOverRef.current.getBoundingClientRect();
            this.setState({ width, height });
        } else if (!this.props.enable && this.state.width && this.state.height) {
            this.setState({width: 0, height: 0});
        }
    }

    /**
     * Component has updated
     */
    public componentDidUpdate(): void {
        if (this.popOverRef && this.popOverRef.current) {
            // Autofocus on popover unless already focused on a descendant of the popover
            if (this.props.autoFocus && (!document.activeElement || !this.popOverRef.current.contains(document.activeElement))) {
                this.popOverRef.current.focus();
            }
            const { width, height } = this.popOverRef.current.getBoundingClientRect();
            if (width !== this.state.width || height !== this.state.height) {
                this.setState({ width, height });
            }
        } else if (!this.props.enable && this.state.width && this.state.height) {
            this.setState({width: 0, height: 0});
        }
    }

    /**
     * Component renderer
     * @public
     * @method
     * @returns {JSX.Element} the popover component rendered in a new portal
     */
    public render(): JSX.Element {
        const { enable, managedClasses, staticPosition, zIndex } = this.props;
        if (!enable) {
            return null;
        }
        let result = (null);
        const zIndexStyle = zIndex != undefined ? {zIndex} : {};
        if (!this.popOverRef || !this.popOverRef.current) {
            result = (
                <div
                    {...this.unhandledProps()}
                    tabIndex={0}
                    ref={this.popOverRef}
                    style={zIndexStyle}
                    className={managedClasses.popOverPlaceholder}>
                    {this.props.children}
                </div>
            );
        } else {
            result = (
                <DesignSystemConsumer>
                    {({ direction }: DesignSystem) => (
                        <div
                            {...this.unhandledProps()}
                            onKeyDown={this.keyDownHandler}
                            tabIndex={0}
                            ref={this.popOverRef}
                            className={managedClasses.popOver}
                            style={{...this.getPlacementStyle(direction), ...zIndexStyle}}>
                            {this.props.children}
                        </div>
                    )}
                </DesignSystemConsumer>
            );
        }

        return staticPosition ? result : ReactDOM.createPortal(result, document.body);
    }

    /** Event handler for escape and tab */
    private keyDownHandler = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const { onDismiss = () => null, anchor } = this.props;
        if (e.keyCode === keyCodeEscape) {
            if (anchor) {
                anchor.focus();
            }
            onDismiss(e);
        } else {
            this.tabEventHandler(e, onDismiss, anchor);
        }
    };

    /**
     * Event handler for tab event
     */
    private tabEventHandler = (
        e: React.KeyboardEvent<HTMLDivElement>,
        onDismiss: (e: React.SyntheticEvent<HTMLDivElement>) => void,
        anchor?: HTMLElement) => {
        if (e.keyCode === keyCodeTab
            && anchor
            && this.popOverRef.current.contains(document.activeElement)) {
            const tabbable: HTMLElement[] = Tabbable(this.popOverRef.current);
            if (e.shiftKey && e.target === tabbable[0]) {
                // focus on anchor if shift+tab is pressed on the first tabbable element
                anchor.focus();
                onDismiss(e);
                e.preventDefault();
            } else if (!e.shiftKey && e.target === tabbable[tabbable.length - 1]) {
                // focus on the next tabbable from anchor if tab is pressed on the last tabbable element
                anchor.focus();
                onDismiss(e);
            }
        }
    };

    /**
     * calculate the placement style for popover
     */
    private getPlacementStyle(pageDirection: Direction): PositionStyles {
        let { left, top, offset, anchor } = this.props;
        const { autoFlipX } = this.props;
        if (anchor && left === undefined && top === undefined) {
            const position = this.determinePosition();
            left = position.left;
            top = position.top;
            offset = position.offset;
        }
        const { width, height } = this.state;
        const windowWidth = getContextData(this.props.requestContext.data.innerWidth, "innerWidth");
        const documentHeight = window.document && window.document.documentElement && window.document.documentElement.offsetHeight;

        if (documentHeight && top + height > documentHeight) {
            top = documentHeight - height - offset;
        }
        // Styles for displaying on left of origin
        const displayOnLeft: PositionStyles = {
            top,
            right: windowWidth - left
        };

        // Styles for displaying on right of origin
        const displayOnRight: PositionStyles = {
            top,
            left: left + offset
        };

        // Prioritise displaying on right for LTR, on left for RTL
        const [displayOnPrioritySide, displayOnNonPrioritySide] =
            pageDirection === Direction.rtl ? [displayOnLeft, displayOnRight] : [displayOnRight, displayOnLeft];

        // Determine which directions have enough space to display full width
        const canFitPrioritySide = (displayOnPrioritySide.left || displayOnPrioritySide.right) + width < windowWidth;
        const canFitNonPrioritySide = (displayOnNonPrioritySide.left || displayOnNonPrioritySide.right) + width < windowWidth;

        // Return non-priority if auto-flip is enabled, and only the priority direction doesn't have space
        return autoFlipX && !canFitPrioritySide && canFitNonPrioritySide ? displayOnNonPrioritySide : displayOnPrioritySide;
    }

    /**
     * determine position based on anchor position
     */
    private determinePosition = (): {top: number, left: number, offset: number} => {
        const { anchor } = this.props;
        if (anchor) {
            const { top, left, width } = anchor.getBoundingClientRect();
            return { top: top + window.scrollY, left: left + window.scrollX, offset: width };
        }
        return { top: window.scrollY, left: window.scrollX, offset: 0 };
    };
}

export default PopOver;
export * from "./PopOver.props";
